/**
 * Optimizer Control Center — one table row
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { Link } from 'react-router-dom';

import { Checkbox } from '@/components/ui';
import { useAuth } from '@/hooks';
import { CopyConfigButton } from '@/features/optimizer-center/clipboard/ui';
import { RowActions } from '@/features/optimizer-center/components/actions/RowActions';
import { PackageAssignCell } from '@/features/flow-optimizer-library/scheduler';
import type { OptimizerRow } from '@/features/optimizer-center/optimizer-center.types';
import { cn } from '@/lib/utils';

import { FormulaSummaryCell } from './FormulaSummaryCell';
import { IntervalCell } from './IntervalCell';
import { NextRunCell } from './NextRunCell';
import { CountryCell, LastRunCell, RowTagCell } from './row-cells';
import { ModeChip, ProviderBadge, StatusChip } from './RowChips';

export interface OptimizerTableRowProps {
  row: OptimizerRow;
  nowMs: number;
  canEditSettings: boolean;
  onEdit: (row: OptimizerRow) => void;
  selectionEnabled: boolean;
  selected: boolean;
  isCopiedSource: boolean;
  onToggleSelect: () => void;
  onCopy: (row: OptimizerRow) => void;
}

export const OptimizerTableRow: FC<OptimizerTableRowProps> = (props) => {
  const { row, nowMs, canEditSettings, onEdit } = props;
  const { selectionEnabled, selected, isCopiedSource, onToggleSelect, onCopy } = props;
  const { hasPermission, user } = useAuth();
  const canEditWeights = hasPermission('flows.edit_weights');
  // /campaigns is admin-only — linking there would just bounce everyone else to /forbidden
  const isAdmin = (user?.role ?? '').toLowerCase() === 'admin';
  const isStandard = row.mode === 'STANDARD';
  return (
    <tr
      data-testid="optimizer-row"
      className={cn(
        'group text-sm transition-colors hover:bg-muted/40',
        isCopiedSource && 'bg-primary/5',
      )}
    >
      {selectionEnabled && (
        <td className="px-2 py-1.5">
          <Checkbox
            aria-label={`Select ${row.campaignName}`}
            checked={selected}
            disabled={isCopiedSource}
            onCheckedChange={onToggleSelect}
          />
        </td>
      )}
      <td className={cn('px-2 py-1.5', isStandard && 'opacity-60')}>
        <ProviderBadge provider={row.provider} />
      </td>
      <td className="px-2 py-1.5 text-center">
        <CountryCell row={row} />
      </td>
      <td className={cn('max-w-[260px] px-2 py-1.5', isStandard && 'opacity-60')}>
        {isAdmin ? (
          <Link
            to={`/campaigns/${row.campaignId}?tab=flows&provider=${row.provider}`}
            className="block truncate text-xs font-medium text-primary hover:underline"
            title={row.campaignName}
          >
            {row.campaignName}
          </Link>
        ) : (
          <span className="block truncate text-xs font-medium text-primary" title={row.campaignName}>
            {row.campaignName}
          </span>
        )}
        <span className="block truncate font-mono text-[10px] text-muted-foreground">
          {row.campaignId}
        </span>
      </td>
      <td className={cn('w-full min-w-[200px] px-2 py-1.5', isStandard && 'opacity-60')}>
        <span className="block truncate text-xs" title={row.flowName}>
          {row.flowName}
        </span>
        <span className="block truncate font-mono text-[10px] text-muted-foreground">
          {row.flowId}
        </span>
      </td>
      <td className={cn('px-2 py-1.5', isStandard && 'opacity-60')}>
        <ModeChip mode={row.mode} />
      </td>
      <td className="px-2 py-1.5">
        <StatusChip row={row} />
      </td>
      <td className="px-2 py-1.5">
        <RowTagCell row={row} canEdit={canEditWeights} />
      </td>
      <td className="px-2 py-1.5">
        <PackageAssignCell row={row} canEdit={canEditSettings} />
      </td>
      <td className="whitespace-nowrap px-2 py-1.5">
        <IntervalCell row={row} canEdit={canEditSettings && !isStandard} />
      </td>
      <td className="whitespace-nowrap px-2 py-1.5">
        <NextRunCell row={row} nowMs={nowMs} />
      </td>
      <td className={cn('whitespace-nowrap px-2 py-1.5 text-xs', isStandard && 'opacity-60')}>
        <LastRunCell row={row} />
      </td>
      <td className={cn('px-2 py-1.5', isStandard && 'opacity-60')}>
        <FormulaSummaryCell row={row} />
      </td>
      <td className="sticky right-0 z-10 bg-card px-2 py-1.5 shadow-[-6px_0_6px_-6px_rgba(0,0,0,0.18)]">
        <div className="flex items-center justify-end gap-1">
          {selectionEnabled && (
            <CopyConfigButton row={row} isCopiedSource={isCopiedSource} onCopy={onCopy} />
          )}
          <RowActions row={row} onEdit={onEdit} />
        </div>
      </td>
    </tr>
  );
};
